import { db } from '../config/firebasedeconfig';
import { 
  doc, 
  setDoc, 
  onSnapshot, 
  updateDoc, 
  deleteDoc, 
  collection, 
  addDoc,
  getDocs,
  query,
  where,
  orderBy 
} from 'firebase/firestore';

// Configuration STUN/TURN servers pour WebRTC - CONFIGURATION AMÉLIORÉE
const ICE_SERVERS = {
  iceServers: [
    // 🔧 Serveurs STUN pour découvrir l'IP publique
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    { urls: 'stun:stun3.l.google.com:19302' },
    { urls: 'stun:stun4.l.google.com:19302' },
    
    // 🔧 Serveurs TURN gratuits pour traverser les NAT stricts
    {
      urls: 'turn:relay1.expressturn.com:3478',
      username: 'efJBIBF2YUDG1PqAAA',
      credential: 'ASdCm01D1kVoHMM'
    },
    {
      urls: 'turn:openrelay.metered.ca:80',
      username: 'openrelayproject',
      credential: 'openrelayproject'
    },
    {
      urls: 'turn:openrelay.metered.ca:443',
      username: 'openrelayproject',
      credential: 'openrelayproject'
    },
    {
      urls: 'turn:openrelay.metered.ca:443?transport=tcp',
      username: 'openrelayproject',
      credential: 'openrelayproject'
    }
  ],
  
  // 🔧 Configuration ICE optimisée
  iceCandidatePoolSize: 10, // Pré-générer des candidats
  bundlePolicy: 'max-bundle', // Optimiser la bande passante
  rtcpMuxPolicy: 'require', // Multiplexer RTCP avec RTP
  iceTransportPolicy: 'all' // Utiliser tous les transports disponibles
};

class WebRTCService {
  constructor() {
    this.peerConnection = null;
    this.localStream = null;
    this.remoteStream = null;
    this.callDocRef = null;
    this.unsubscribeCall = null;
    this.unsubscribeOfferCandidates = null;
    this.unsubscribeAnswerCandidates = null;
    this.isCallActive = false;
    this.isCaller = false;
    this.currentCallId = null;
    
    // 🔧 AMÉLIORATION : Queue pour les candidats ICE précoces
    this.iceCandidateQueue = [];
    this.isRemoteDescriptionSet = false;
    this.iceGatheringComplete = false;
    
    // Callbacks pour les événements
    this.onRemoteStreamReceived = null;
    this.onCallStateChange = null;
    this.onError = null;
  }

  // Générer un ID d'appel unique
  generateCallId() {
    return `call_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Initialiser la connexion WebRTC
  async initializePeerConnection() {
    try {
      const role = this.isCaller ? '[WebRTC_CALLER]' : '[WebRTC_CALLEE]';
      console.log(`${role} 🔧 Initialisation PeerConnection avec configuration ICE avancée...`);
      this.peerConnection = new RTCPeerConnection(ICE_SERVERS);
      
      // 🔧 AMÉLIORATION : Réinitialiser les états ICE
      this.iceCandidateQueue = [];
      this.isRemoteDescriptionSet = false;
      this.iceGatheringComplete = false;
      
      // Gérer la réception du stream distant
      this.peerConnection.ontrack = (event) => {
        const role = this.isCaller ? '[WebRTC_CALLER]' : '[WebRTC_CALLEE]';
        console.log(`${role} 🎥 ontrack event:`, {
          track: event.track,
          streams: event.streams,
          transceiver: event.transceiver
        });
        
        if (event.streams && event.streams[0]) {
          console.log(`${role} 🎥 Stream distant reçu:`, event.streams[0]);
          this.remoteStream = event.streams[0];
          
          // 🔧 NOUVEAU : Vérifier l'état du stream
          console.log(`${role} 🎥 État du stream distant:`, {
            id: this.remoteStream.id,
            active: this.remoteStream.active,
            audioTracks: this.remoteStream.getAudioTracks().length,
            videoTracks: this.remoteStream.getVideoTracks().length
          });
          
          // 🔧 NOUVEAU : Vérifier chaque track
          this.remoteStream.getTracks().forEach((track, index) => {
            console.log(`${role} 🎥 Track ${index}:`, {
              kind: track.kind,
              enabled: track.enabled,
              muted: track.muted,
              readyState: track.readyState,
              label: track.label
            });
          });
          
          if (this.onRemoteStreamReceived) {
            this.onRemoteStreamReceived(this.remoteStream);
          }
        } else {
          console.warn(`${role} ⚠️ ontrack sans stream !`);
          
          // 🔧 NOUVEAU : Créer un stream si nécessaire
          if (event.track) {
            console.log(`${role} 🎥 Création d'un stream pour le track:`, event.track.kind);
            if (!this.remoteStream) {
              this.remoteStream = new MediaStream();
            }
            this.remoteStream.addTrack(event.track);
            
            // Notifier quand on a au moins une vidéo et un audio
            const hasVideo = this.remoteStream.getVideoTracks().length > 0;
            const hasAudio = this.remoteStream.getAudioTracks().length > 0;
            
            console.log(`${role} 🎥 Stream status:`, { hasVideo, hasAudio });
            
            if (hasVideo && this.onRemoteStreamReceived) {
              this.onRemoteStreamReceived(this.remoteStream);
            }
          }
        }
      };

      // 🔧 AMÉLIORATION : Gestion optimisée des candidats ICE
      this.peerConnection.onicecandidate = async (event) => {
        console.log('[WebRTC] 🧊 Événement ICE candidate:', event.candidate ? 'nouveau candidat' : 'null (fin)');
        
        if (event.candidate) {
          const candidateData = {
            candidate: event.candidate.toJSON(),
            timestamp: Date.now(),
            type: event.candidate.type, // host, srflx, relay, prflx
            protocol: event.candidate.protocol, // udp, tcp
            address: event.candidate.address,
            port: event.candidate.port
          };
          
          console.log('[WebRTC] 🧊 Type candidat:', candidateData.type, candidateData.protocol);
          
          // Si la remote description n'est pas encore définie, mettre en queue
          if (!this.isRemoteDescriptionSet && this.callDocRef) {
            console.log('[WebRTC] 🧊 Candidat mis en queue (remote description pas encore définie)');
            this.iceCandidateQueue.push(candidateData);
          }
          
          // Envoyer le candidat via Firestore si callDocRef existe
          if (this.callDocRef) {
            try {
              const candidatesCollection = this.isCaller ? 'offerCandidates' : 'answerCandidates';
              await addDoc(collection(this.callDocRef, candidatesCollection), candidateData);
              console.log('[WebRTC] ✅ Candidat ICE envoyé:', candidateData.type);
            } catch (error) {
              console.error('[WebRTC] ❌ Erreur envoi candidat ICE:', error);
            }
          }
        } else {
          // Candidat null = fin de la collecte ICE
          console.log('[WebRTC] 🏁 Collecte ICE terminée');
          this.iceGatheringComplete = true;
        }
      };

      // 🔧 AMÉLIORATION : Gestion des états ICE
      this.peerConnection.onicegatheringstatechange = () => {
        console.log('[WebRTC] 🧊 État collecte ICE:', this.peerConnection.iceGatheringState);
      };
      
      this.peerConnection.oniceconnectionstatechange = () => {
        console.log('[WebRTC] 🧊 État connexion ICE:', this.peerConnection.iceConnectionState);
        
        // Gestion des états de connexion ICE
        switch (this.peerConnection.iceConnectionState) {
          case 'checking':
            console.log('[WebRTC] 🔍 Vérification des candidats ICE...');
            break;
          case 'connected':
            console.log('[WebRTC] ✅ Connexion ICE établie');
            break;
          case 'completed':
            console.log('[WebRTC] 🎉 Connexion ICE optimisée');
            break;
          case 'failed':
            console.error('[WebRTC] ❌ Échec connexion ICE');
            if (this.onError) {
              this.onError('Échec de la connexion réseau. Vérifiez votre connexion internet.');
            }
            break;
          case 'disconnected':
            console.warn('[WebRTC] ⚠️ Connexion ICE interrompue');
            break;
          case 'closed':
            console.log('[WebRTC] 🔒 Connexion ICE fermée');
            break;
        }
      };

      // Gérer les changements d'état de connexion
      this.peerConnection.onconnectionstatechange = () => {
        console.log('[WebRTC] État de connexion:', this.peerConnection.connectionState);
        
        // 🔧 CORRECTION : Envoyer l'état 'connected' quand WebRTC est connecté
        if (this.peerConnection.connectionState === 'connected') {
          console.log('[WebRTC] 🎉 Connexion WebRTC établie !');
          if (this.onCallStateChange) {
            this.onCallStateChange('connected');
          }
        } else if (this.peerConnection.connectionState === 'failed' ||
                   this.peerConnection.connectionState === 'closed') {
          if (this.onCallStateChange) {
            this.onCallStateChange(this.peerConnection.connectionState);
          }
          // Laisser la logique d'arrêt côté interface (pas de cleanup agressif ici)
        } else if (this.peerConnection.connectionState === 'disconnected') {
          if (this.onCallStateChange) {
            this.onCallStateChange('disconnected');
          }
        }
      };

      console.log('[WebRTC] ✅ PeerConnection initialisée avec succès');
    } catch (error) {
      console.error('[WebRTC] ❌ Erreur lors de l\'initialisation:', error);
      if (this.onError) {
        this.onError('Erreur d\'initialisation WebRTC: ' + error.message);
      }
      throw error;
    }
  }

  // Obtenir l'accès à la caméra et au microphone
  async getUserMedia(constraints = { video: true, audio: true }) {
    let retryCount = 0;
    const maxRetries = 3;

    const ensureAtLeastOneMediaRequested = (mediaConstraints) => {
      if (mediaConstraints?.audio === false && mediaConstraints?.video === false) {
        const noDevicesError = new Error('Aucune caméra ou microphone détecté. Vérifiez votre équipement.');
        noDevicesError.name = 'NotFoundError';
        throw noDevicesError;
      }
    };

    const tryGetUserMedia = async (mediaConstraints) => {
      ensureAtLeastOneMediaRequested(mediaConstraints);
      return navigator.mediaDevices.getUserMedia(mediaConstraints);
    };
    
    while (retryCount < maxRetries) {
      try {
        console.log(`[WebRTC] Tentative ${retryCount + 1}/${maxRetries} d'accès aux médias...`);

        // 🔧 AMÉLIORATION : Adapter les contraintes aux périphériques réellement disponibles
        let resolvedConstraints = { ...constraints };
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          // Sur certains navigateurs mobiles, enumerateDevices peut être vide tant qu'aucun getUserMedia n'a été accordé.
          if (!devices || devices.length === 0) {
            console.warn('[WebRTC] ⚠️ enumerateDevices vide, on conserve les contraintes demandées');
          } else {
          const hasAudioInput = devices.some((d) => d.kind === 'audioinput');
          const hasVideoInput = devices.some((d) => d.kind === 'videoinput');

          if (resolvedConstraints.audio && !hasAudioInput) {
            console.warn('[WebRTC] ⚠️ Aucun micro détecté, démarrage sans audio');
            resolvedConstraints.audio = false;
          }
          if (resolvedConstraints.video && !hasVideoInput) {
            console.warn('[WebRTC] ⚠️ Aucune caméra détectée, démarrage sans vidéo');
            resolvedConstraints.video = false;
          }

          if (!resolvedConstraints.audio && !resolvedConstraints.video) {
            const noDevicesError = new Error('Aucune caméra ou microphone détecté. Vérifiez votre équipement.');
            noDevicesError.name = 'NotFoundError';
            throw noDevicesError;
          }
          }
        } catch (enumerateError) {
          if (enumerateError?.name === 'NotFoundError') {
            throw enumerateError;
          }
          console.warn('[WebRTC] ⚠️ enumerateDevices a échoué, contraintes inchangées:', enumerateError?.message || enumerateError);
        }
        
        // 🔧 AMÉLIORATION : Contraintes vidéo optimisées
        const enhancedConstraints = {
          audio: resolvedConstraints.audio === true ? {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          } : resolvedConstraints.audio,
          video: resolvedConstraints.video === true ? {
            width: { min: 640, ideal: 1280, max: 1920 },
            height: { min: 480, ideal: 720, max: 1080 },
            frameRate: { min: 15, ideal: 30, max: 60 },
            facingMode: 'user' // Préférer la caméra frontale
          } : resolvedConstraints.video
        };

        ensureAtLeastOneMediaRequested(enhancedConstraints);
        
        // 🔧 NOUVELLE STRATÉGIE : Essayer d'abord avec contraintes complètes
        try {
          console.log('[WebRTC] Essai avec contraintes complètes:', enhancedConstraints);
          this.localStream = await tryGetUserMedia(enhancedConstraints);
          
          // 🔧 VÉRIFICATION : Si c'est FS13FF-183-2, appliquer des corrections
          const videoTrack = this.localStream.getVideoTracks()[0];
          if (videoTrack && videoTrack.label && videoTrack.label.includes('FS13FF-183-2')) {
            console.log('[WebRTC] 🎥 Détection FS13FF-183-2, application de contraintes spéciales...');
            
            // Arrêter le stream actuel
            this.localStream.getTracks().forEach(track => track.stop());
            
            // Réessayer avec des contraintes minimales pour cette caméra
            const fs13ffConstraints = {
              audio: resolvedConstraints.audio,
              video: {
                width: { exact: 640 },
                height: { exact: 480 },
                frameRate: { exact: 30 }
              }
            };
            
            console.log('[WebRTC] 🎥 Contraintes spéciales FS13FF-183-2:', fs13ffConstraints);
            this.localStream = await tryGetUserMedia(fs13ffConstraints);
          }
        } catch (error) {
          console.warn('[WebRTC] ⚠️ Échec avec contraintes complètes, essai avec contraintes minimales...');

          // 🔧 Fallback: si NotFoundError, tenter en désactivant le média manquant
          if (error?.name === 'NotFoundError') {
            // Cas fréquent: micro absent/indisponible -> essayer vidéo seule, puis audio seul
            if (resolvedConstraints.video && resolvedConstraints.audio) {
              try {
                console.warn('[WebRTC] 🔁 NotFoundError: retry vidéo seule (audio=false)');
                this.localStream = await tryGetUserMedia({ ...enhancedConstraints, audio: false });
              } catch (videoOnlyError) {
                console.warn('[WebRTC] ⚠️ Échec vidéo seule:', videoOnlyError?.message || videoOnlyError);
                console.warn('[WebRTC] 🔁 NotFoundError: retry audio seul (video=false)');
                this.localStream = await tryGetUserMedia({ ...enhancedConstraints, video: false });
              }
              // Succès -> sortir du catch
            } else if (resolvedConstraints.audio) {
              console.warn('[WebRTC] 🔁 NotFoundError: retry sans micro');
              resolvedConstraints.audio = false;
            } else if (resolvedConstraints.video) {
              console.warn('[WebRTC] 🔁 NotFoundError: retry sans caméra');
              resolvedConstraints.video = false;
            }
          }

          // Si un stream a été récupéré via le fallback ci-dessus, continuer
          if (this.localStream) {
            // noop
          } else {
          
          // 🔧 NOUVEAU : Essayer de trouver une meilleure caméra
          try {
            if (!resolvedConstraints.video) {
              throw new Error('Aucune caméra disponible');
            }

            const bestCamera = await this.getBestCamera();
            const betterConstraints = {
              audio: resolvedConstraints.audio,
              video: {
                deviceId: { exact: bestCamera.deviceId }
              }
            };

            ensureAtLeastOneMediaRequested(betterConstraints);
            
            console.log('[WebRTC] 🎯 Essai avec caméra spécifique:', bestCamera.label);
            this.localStream = await tryGetUserMedia(betterConstraints);
          } catch (cameraError) {
            console.warn('[WebRTC] ⚠️ Échec avec caméra spécifique:', cameraError.message);
            
            // Dernier recours : contraintes minimales
            const minimalConstraints = {
              audio: resolvedConstraints.audio,
              video: resolvedConstraints.video === true ? true : resolvedConstraints.video
            };

            ensureAtLeastOneMediaRequested(minimalConstraints);
            
            console.log('[WebRTC] Essai avec contraintes minimales:', minimalConstraints);
            this.localStream = await tryGetUserMedia(minimalConstraints);
          }
          }
        }
        
        console.log('[WebRTC] ✅ Accès aux médias accordé !');
        
        // 🔧 NOUVEAU : Détecter l'écran vert
        const videoTracks = this.localStream.getVideoTracks();
        if (videoTracks.length > 0) {
          console.log('[WebRTC] 🎨 Vérification de l\'écran vert...');
          
          // Créer une copie du stream pour le test
          const testStream = this.localStream.clone();
          const isGreenScreen = await this.detectGreenScreen(testStream);
          
          if (isGreenScreen) {
            console.warn('[WebRTC] ⚠️ ÉCRAN VERT DÉTECTÉ ! Passage en mode audio uniquement.');
            
            // Arrêter les pistes vidéo
            this.localStream.getVideoTracks().forEach(track => {
              track.stop();
              this.localStream.removeTrack(track);
            });
            
            // Informer l'utilisateur
            if (this.onError) {
              this.onError('🎥 Problème détecté avec votre caméra (écran vert). L\'appel continue en mode audio uniquement.');
            }
            
            // Continuer avec audio seulement
            console.log('[WebRTC] 🎤 Mode audio uniquement activé');
          }
        }
        
        // 🔧 DEBUG : Afficher les informations sur les pistes
        const finalVideoTracks = this.localStream.getVideoTracks();
        const audioTracks = this.localStream.getAudioTracks();
        
        console.log('[WebRTC] 📹 Pistes vidéo:', finalVideoTracks.length);
        finalVideoTracks.forEach((track, index) => {
          console.log(`[WebRTC] 📹 Vidéo ${index}:`, {
            label: track.label,
            enabled: track.enabled,
            muted: track.muted,
            readyState: track.readyState,
            settings: track.getSettings()
          });
        });
        
        console.log('[WebRTC] 🎤 Pistes audio:', audioTracks.length);
        audioTracks.forEach((track, index) => {
          console.log(`[WebRTC] 🎤 Audio ${index}:`, {
            label: track.label,
            enabled: track.enabled,
            muted: track.muted,
            readyState: track.readyState
          });
        });
        
        // Attacher les pistes à la PeerConnection (évite de créer des m-lines supplémentaires côté répondant)
        if (this.peerConnection) {
          console.log('[WebRTC] 🔌 État PeerConnection avant attach tracks:', this.peerConnection.signalingState);

          const transceivers = this.peerConnection.getTransceivers ? this.peerConnection.getTransceivers() : [];

          for (const track of this.localStream.getTracks()) {
            console.log('[WebRTC] 🔌 Attach piste:', track.kind, track.label);

            // 1) Priorité : transceiver existant (créé par setRemoteDescription) -> replaceTrack
            const matchingTransceiver = transceivers.find((t) => {
              const receiverKind = t?.receiver?.track?.kind;
              const senderKind = t?.sender?.track?.kind;
              return receiverKind === track.kind && (!senderKind || senderKind === track.kind);
            });

            if (matchingTransceiver) {
              try {
                await matchingTransceiver.sender.replaceTrack(track);
                if (matchingTransceiver.direction === 'recvonly') {
                  matchingTransceiver.direction = 'sendrecv';
                }
                continue;
              } catch (replaceError) {
                console.warn('[WebRTC] ⚠️ replaceTrack(transceiver) échoué, fallback:', replaceError?.message || replaceError);
              }
            }

            // 2) Sinon : sender existant du même type
            const existingSender = this.peerConnection.getSenders().find((s) => s.track && s.track.kind === track.kind);
            if (existingSender) {
              try {
                await existingSender.replaceTrack(track);
                continue;
              } catch (senderReplaceError) {
                console.warn('[WebRTC] ⚠️ replaceTrack(sender) échoué, fallback:', senderReplaceError?.message || senderReplaceError);
              }
            }

            // 3) Dernier recours : addTrack
            try {
              this.peerConnection.addTrack(track, this.localStream);
            } catch (addTrackError) {
              console.error('[WebRTC] ❌ addTrack échoué:', addTrackError);
            }
          }

          const senders = this.peerConnection.getSenders();
          console.log('[WebRTC] 📤 Senders après attach:', senders.map(sender => ({
            track: sender.track ? {
              kind: sender.track.kind,
              enabled: sender.track.enabled,
              readyState: sender.track.readyState
            } : null
          })));
        } else {
          console.warn('[WebRTC] ⚠️ PeerConnection non initialisée, tracks non attachées');
        }
        
        return this.localStream;
        
      } catch (error) {
        console.error(`[WebRTC] ❌ Tentative ${retryCount + 1} échouée:`, error);
        console.error('[WebRTC] Type d\'erreur:', error.name);
        console.error('[WebRTC] Message:', error.message);
        
        retryCount++;
        
        if (retryCount >= maxRetries) {
          const errorMessage = this.getDetailedMediaErrorMessage(error);
          console.error('[WebRTC] ❌ ÉCHEC DÉFINITIF:', errorMessage);
          
          if (this.onError) {
            this.onError(errorMessage);
          }
          throw new Error(errorMessage);
        }
        
        // Attendre avant de réessayer
        console.log(`[WebRTC] ⏳ Attente de ${retryCount} secondes avant nouvelle tentative...`);
        await new Promise(resolve => setTimeout(resolve, retryCount * 1000));
      }
    }
  }

  // Messages d'erreur personnalisés pour les médias
  getMediaErrorMessage(error) {
    switch (error.name) {
      case 'NotAllowedError':
        return 'Accès à la caméra/microphone refusé. Veuillez autoriser l\'accès dans les paramètres.';
      case 'NotFoundError':
        return 'Aucune caméra ou microphone détecté. Vérifiez votre équipement.';
      case 'NotSupportedError':
        return 'Votre navigateur ne supporte pas les appels vidéo.';
      case 'OverconstrainedError':
        return 'Paramètres de caméra/microphone incompatibles.';
      default:
        return `Erreur d'accès aux médias: ${error.message}`;
    }
  }

  // 🔧 NOUVELLE FONCTION : Messages d'erreur détaillés
  getDetailedMediaErrorMessage(error) {
    const baseMessage = this.getMediaErrorMessage(error);
    
    const solutions = {
      'NotReadableError': [
        '🔍 Vérifiez qu\'aucune autre application n\'utilise votre caméra (Skype, Zoom, Teams, Discord, etc.)',
        '🔄 Fermez tous les autres onglets du navigateur qui pourraient utiliser la caméra',
        '🔌 Débranchez et rebranchez votre caméra si c\'est une webcam externe',
        '💻 Redémarrez votre navigateur',
        '🛠️ Vérifiez les paramètres de confidentialité de Windows pour la caméra'
      ],
      'NotAllowedError': [
        '🔓 Cliquez sur l\'icône de cadenas dans la barre d\'adresse',
        '✅ Autorisez l\'accès à la caméra et au microphone',
        '🔄 Rafraîchissez la page après avoir donné les permissions'
      ],
      'NotFoundError': [
        '📹 Vérifiez que votre caméra est bien branchée',
        '💻 Installez les drivers de votre caméra',
        '🔍 Vérifiez dans le Gestionnaire de périphériques que la caméra est détectée'
      ]
    };
    
    const errorSolutions = solutions[error.name] || ['🔄 Essayez de rafraîchir la page'];
    
    return `${baseMessage}\n\n💡 Solutions possibles :\n${errorSolutions.join('\n')}`;
  }

  // Créer un nouvel appel (côté appelant) - PROCESSUS OPTIMISÉ
  async createCall(callerId, calleeId, callerData = {}) {
    try {
      console.log('[WebRTC] 🎯 DÉMARRAGE CRÉATION APPEL - PROCESSUS OPTIMISÉ');
      console.log('[WebRTC] 📞 callerId:', callerId);
      console.log('[WebRTC] 📞 calleeId:', calleeId);
      console.log('[WebRTC] 📞 callerData:', callerData);
      
      this.isCaller = true;
      this.currentCallId = this.generateCallId();
      console.log('[WebRTC] 🆔 callId généré:', this.currentCallId);
      
      // 🔧 ÉTAPE 1 : Initialiser WebRTC ET obtenir les médias
      await this.initializePeerConnection();
      await this.getUserMedia();
      
      // 🔧 NOUVEAU : Vérifier que les tracks sont bien ajoutées
      const senders = this.peerConnection.getSenders();
      console.log('[WebRTC] 📤 Vérification senders avant offre:', {
        count: senders.length,
        tracks: senders.map(s => s.track ? s.track.kind : 'no track')
      });
      
      if (senders.length === 0) {
        console.error('[WebRTC] ❌ Aucun sender ! Réajout des tracks...');
        this.localStream.getTracks().forEach(track => {
          this.peerConnection.addTrack(track, this.localStream);
        });
      }
      
      // 🔧 ÉTAPE 2 : Créer le document d'appel dans Firestore AVANT l'offre
      this.callDocRef = doc(db, 'calls', this.currentCallId);
      const callDocument = {
        callerId,
        calleeId,
        callerData,
        status: 'ringing',
        createdAt: Date.now(),
        lastActivity: Date.now()
      };
      
      console.log('[WebRTC] 📄 Document à créer:', callDocument);
      await setDoc(this.callDocRef, callDocument);
      console.log('[WebRTC] ✅ Document d\'appel créé avec succès dans Firestore');

      // 🔧 ÉTAPE 3 : Démarrer l'écoute des candidats ICE AVANT de créer l'offre
      this.listenForRemoteCandidates();
      console.log('[WebRTC] 🎧 Écoute des candidats ICE distants activée');

      // 🔧 ÉTAPE 4 : Créer l'offre WebRTC avec contraintes optimisées
      console.log('[WebRTC] 🔨 Création de l\'offre WebRTC...');
      
      // 🔧 NOUVEAU : Vérifier les transceivers avant l'offre
      const transceivers = this.peerConnection.getTransceivers();
      console.log('[WebRTC] 📡 Transceivers avant offre:', transceivers.map(t => ({
        mid: t.mid,
        direction: t.direction,
        currentDirection: t.currentDirection,
        sender: {
          track: t.sender.track ? {
            kind: t.sender.track.kind,
            enabled: t.sender.track.enabled
          } : null
        }
      })));
      
      const offerOptions = {
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
        voiceActivityDetection: true,
        iceRestart: false
      };
      
      const offer = await this.peerConnection.createOffer(offerOptions);
      
      // 🔧 NOUVEAU : Log de l'offre SDP
      console.log('[WebRTC] 📝 Offre créée, contient vidéo:', offer.sdp.includes('m=video'));
      console.log('[WebRTC] 📝 Offre créée, contient audio:', offer.sdp.includes('m=audio'));
      
      // 🔧 ÉTAPE 5 : Définir la description locale
      await this.peerConnection.setLocalDescription(offer);
      console.log('[WebRTC] 📝 Description locale définie');
      
      // 🔧 ÉTAPE 6 : Sauvegarder l'offre dans Firestore
      await updateDoc(this.callDocRef, {
        offer: {
          type: offer.type,
          sdp: offer.sdp
        },
        offerCreatedAt: Date.now()
      });

      console.log('[WebRTC] ✅ Offre WebRTC créée et sauvegardée');

      // 🔧 ÉTAPE 7 : Écouter la réponse
      this.listenForAnswer();
      
      // 🔧 ÉTAPE 8 : Traiter les candidats ICE en queue après création de l'offre
      await this.processQueuedCandidates();

      console.log('[WebRTC] 🎉 APPEL CRÉÉ AVEC SUCCÈS, ID:', this.currentCallId);
      return this.currentCallId;
    } catch (error) {
      console.error('[WebRTC] ❌ ERREUR CRÉATION APPEL:', error);
      console.error('[WebRTC] ❌ Code erreur:', error.code);
      console.error('[WebRTC] ❌ Message:', error.message);
      await this.cleanup();
      throw error;
    }
  }

  // Accepter un appel entrant (côté appelé) - PROCESSUS OPTIMISÉ
  async acceptCall(callId, calleeData = {}) {
    try {
      console.log('[WebRTC] 🎯 DÉMARRAGE ACCEPTATION APPEL - PROCESSUS OPTIMISÉ');
      console.log('[WebRTC] 📞 callId:', callId);
      console.log('[WebRTC] 📞 calleeData:', calleeData);
      
      this.isCaller = false;
      this.currentCallId = callId; // 🔧 CRITIQUE : Stocker l'ID de l'appel
      
      // 🔧 ÉTAPE 1 : Initialiser WebRTC
      await this.initializePeerConnection();
      
      // 🔧 ÉTAPE 2 : Référence au document d'appel
      this.callDocRef = doc(db, 'calls', callId);
      
      // 🔧 ÉTAPE 3 : Démarrer l'écoute des candidats ICE AVANT de récupérer l'offre
      this.listenForRemoteCandidates();
      console.log('[WebRTC] 🎧 Écoute des candidats ICE distants activée');

      let mediaInitialized = false;
      
      // 🔧 ÉTAPE 4 : Récupérer l'offre et créer la réponse
      const unsubscribe = onSnapshot(this.callDocRef, async (snapshot) => {
        const data = snapshot.data();
        console.log('[WebRTC] 📄 Données d\'appel reçues:', data);
        
        if (!data || !data.offer) {
          console.error('[WebRTC] ❌ Pas d\'offre trouvée dans le document');
          return;
        }
        
        if (!this.peerConnection.currentRemoteDescription && data.offer) {
          console.log('[WebRTC] 🔨 Traitement de l\'offre...');
          
          try {
            // 🔧 ÉTAPE 5 : Définir la description distante (offre)
            const offer = new RTCSessionDescription(data.offer);
            await this.peerConnection.setRemoteDescription(offer);
            console.log('[WebRTC] 📝 Description distante (offre) définie');
            
            // 🔧 IMPORTANT : Marquer que la remote description est définie
            this.isRemoteDescriptionSet = true;

            // 🔧 IMPORTANT : Initialiser les médias APRÈS setRemoteDescription pour matcher les m-lines
            if (!mediaInitialized) {
              mediaInitialized = true;
              await this.getUserMedia();
            }
            
            // 🔧 ÉTAPE 6 : Créer la réponse avec contraintes optimisées
            const answerOptions = {
              offerToReceiveAudio: true,
              offerToReceiveVideo: true,
              voiceActivityDetection: true
            };
            
            // 🔧 NOUVEAU : Vérifier les transceivers avant la réponse
            const transceivers = this.peerConnection.getTransceivers();
            console.log('[WebRTC] 📡 Transceivers avant réponse:', transceivers.map(t => ({
              mid: t.mid,
              direction: t.direction,
              currentDirection: t.currentDirection,
              receiver: {
                track: t.receiver.track ? {
                  kind: t.receiver.track.kind,
                  readyState: t.receiver.track.readyState
                } : null
              }
            })));
            
            const answer = await this.peerConnection.createAnswer(answerOptions);
            
            // 🔧 NOUVEAU : Log de la réponse SDP
            console.log('[WebRTC] 📝 Réponse créée, contient vidéo:', answer.sdp.includes('m=video'));
            console.log('[WebRTC] 📝 Réponse créée, contient audio:', answer.sdp.includes('m=audio'));
            
            await this.peerConnection.setLocalDescription(answer);
            console.log('[WebRTC] 📝 Réponse créée et description locale définie');
            
            // 🔧 ÉTAPE 7 : Sauvegarder la réponse et mettre à jour le statut
            await updateDoc(this.callDocRef, {
              answer: {
                type: answer.type,
                sdp: answer.sdp
              },
              calleeData,
              status: 'connected', // 🔧 IMPORTANT : Marquer comme connecté
              answeredAt: Date.now()
            });
            
            console.log('[WebRTC] ✅ Réponse sauvegardée et statut mis à jour');
            
            // 🔧 ÉTAPE 8 : Traiter les candidats ICE en queue
            await this.processQueuedCandidates();
            
            // Se désabonner après avoir traité l'offre
            unsubscribe();
          } catch (error) {
            console.error('[WebRTC] ❌ Erreur traitement offre:', error);
            throw error;
          }
        }
      });
      
      console.log('[WebRTC] 🎉 APPEL ACCEPTÉ AVEC SUCCÈS');
      return true;
    } catch (error) {
      console.error('[WebRTC] ❌ ERREUR ACCEPTATION APPEL:', error);
      await this.cleanup();
      throw error;
    }
  }

  // 🔧 AMÉLIORATION : Écouter la réponse à l'offre avec gestion optimisée
  listenForAnswer() {
    if (!this.callDocRef) return;

    console.log('[WebRTC] 🎧 Écoute de la réponse à l\'offre...');
    
    this.unsubscribeCall = onSnapshot(this.callDocRef, async (snapshot) => {
      const data = snapshot.data();
      
      if (data && data.answer) {
        if (!this.peerConnection) {
          console.warn('[WebRTC] peerConnection null, impossible de traiter la réponse', {
            callId: this.currentCallId
          });
          return;
        }
        // Log debug AVANT la condition
        console.log('[WebRTC] [DEBUG] Test condition answer:', {
          signalingState: this.peerConnection.signalingState,
          callId: this.currentCallId
        });
        if (this.peerConnection.signalingState !== 'stable') {
          console.log('[WebRTC] 📩 Réponse reçue et traitée:', {
            type: data.answer.type,
            callId: this.currentCallId,
            signalingState: this.peerConnection.signalingState
          });
          try {
            await this.peerConnection.setRemoteDescription(new RTCSessionDescription(data.answer));
            this.isRemoteDescriptionSet = true;
            console.log('[WebRTC] ✅ Remote description (réponse) définie', {
              callId: this.currentCallId,
              signalingState: this.peerConnection.signalingState
            });
            // 🔧 Traiter les candidats ICE en queue maintenant que la remote description est définie
            await this.processQueuedCandidates();
            this.isCallActive = true;
            // 🔧 Mettre à jour le statut dans Firestore
            await updateDoc(this.callDocRef, {
              status: 'connected',
              connectedAt: Date.now(),
              callerConnectedAt: Date.now()
            });
            console.log('[WebRTC] 🎉 Connexion WebRTC établie côté appelant');
          } catch (error) {
            console.error('[WebRTC] ❌ Erreur lors du traitement de la réponse:', error);
            if (this.onError) {
              this.onError('Erreur lors de l\'établissement de la connexion: ' + error.message);
            }
          }
        } else {
          console.warn('[WebRTC] ⚠️ Remote description (answer) ignorée : signalingState déjà stable', {
            callId: this.currentCallId,
            signalingState: this.peerConnection.signalingState
          });
        }
      }
      
      // 🔧 Surveiller les changements de statut
      if (data && data.status) {
        switch (data.status) {
          case 'rejected':
            console.log('[WebRTC] 📞 Appel refusé par le destinataire');
            if (this.onCallStateChange) {
              this.onCallStateChange('rejected');
            }
            await this.cleanup();
            break;
          case 'ended':
            console.log('[WebRTC] 📞 Appel terminé par le destinataire');
            if (this.onCallStateChange) {
              this.onCallStateChange('ended');
            }
            await this.cleanup();
            break;
        }
      }
    }, (error) => {
      console.error('[WebRTC] ❌ Erreur listener réponse:', error);
      if (this.onError) {
        this.onError('Erreur de communication: ' + error.message);
      }
    });
  }

  // 🔧 AMÉLIORATION : Écouter les candidats ICE distants avec gestion optimisée
  listenForRemoteCandidates() {
    if (!this.callDocRef) return;

    const candidatesCollection = this.isCaller ? 'answerCandidates' : 'offerCandidates';
    console.log('[WebRTC] 🎧 Écoute des candidats ICE distants dans:', candidatesCollection);
    
    this.unsubscribeAnswerCandidates = onSnapshot(
      collection(this.callDocRef, candidatesCollection),
      (snapshot) => {
        snapshot.docChanges().forEach(async (change) => {
          if (change.type === 'added') {
            const candidateData = change.doc.data();
            console.log('[WebRTC] 🧊 Nouveau candidat distant reçu:', candidateData.type, candidateData.protocol);
            
            try {
              const candidate = new RTCIceCandidate(candidateData.candidate);
              
              // Vérifier que la connexion est prête pour recevoir des candidats
              if (this.peerConnection && this.peerConnection.remoteDescription) {
                await this.peerConnection.addIceCandidate(candidate);
                console.log('[WebRTC] ✅ Candidat ICE distant ajouté:', candidateData.type);
              } else {
                console.log('[WebRTC] ⏳ Remote description pas encore définie, candidat en attente');
                // Le candidat sera traité quand setRemoteDescription sera appelé
              }
            } catch (error) {
              console.error('[WebRTC] ❌ Erreur lors de l\'ajout du candidat ICE distant:', error);
              // Ne pas faire échouer l'appel pour un candidat défaillant
            }
          }
        });
      },
      (error) => {
        console.error('[WebRTC] ❌ Erreur listener candidats ICE:', error);
      }
    );
  }
  
  // 🔧 NOUVELLE FONCTION : Traiter la queue des candidats ICE en attente
  async processQueuedCandidates() {
    if (!this.isRemoteDescriptionSet || this.iceCandidateQueue.length === 0) {
      return;
    }
    
    console.log('[WebRTC] 🔄 Traitement de', this.iceCandidateQueue.length, 'candidats en queue');
    
    for (const candidateData of this.iceCandidateQueue) {
      try {
        if (this.callDocRef) {
          const candidatesCollection = this.isCaller ? 'offerCandidates' : 'answerCandidates';
          await addDoc(collection(this.callDocRef, candidatesCollection), candidateData);
          console.log('[WebRTC] ✅ Candidat en queue envoyé:', candidateData.type);
        }
      } catch (error) {
        console.error('[WebRTC] ❌ Erreur envoi candidat en queue:', error);
      }
    }
    
    // Vider la queue
    this.iceCandidateQueue = [];
    console.log('[WebRTC] 🧹 Queue des candidats vidée');
  }

  // Refuser un appel
  async rejectCall(callId, reason = 'Appel refusé') {
    try {
      console.log('[WebRTC] Refus de l\'appel:', callId, reason);
      const callRef = doc(db, 'calls', callId);
      await updateDoc(callRef, {
        status: 'rejected',
        rejectedAt: Date.now(),
        rejectReason: reason
      });
      
      // Nettoyer après un délai
      setTimeout(async () => {
        try {
          await deleteDoc(callRef);
        } catch (error) {
          console.error('[WebRTC] Erreur lors de la suppression du document:', error);
        }
      }, 5000);

      await this.cleanup();
    } catch (error) {
      console.error('[WebRTC] Erreur lors du refus:', error);
    }
  }

  // Terminer un appel
  async hangupCall() {
    try {
      console.log('[WebRTC] Fin de l\'appel');
      if (this.callDocRef && this.currentCallId) {
        await updateDoc(this.callDocRef, {
          status: 'ended',
          endedAt: Date.now()
        });

        // Supprimer le document après un délai
        setTimeout(async () => {
          try {
            await deleteDoc(this.callDocRef);
          } catch (error) {
            console.error('[WebRTC] Erreur lors de la suppression:', error);
          }
        }, 2000);
      }
      
      await this.cleanup();
    } catch (error) {
      console.error('[WebRTC] Erreur lors de la fin d\'appel:', error);
      await this.cleanup();
    }
  }

  // Basculer le microphone
  toggleMute() {
    if (this.localStream) {
      const audioTrack = this.localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        return !audioTrack.enabled; // retourne true si muté
      }
    }
    return false;
  }

  // Basculer la caméra
  toggleVideo() {
    if (this.localStream) {
      const videoTrack = this.localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        return !videoTrack.enabled; // retourne true si caméra éteinte
      }
    }
    return false;
  }

  // 🔧 AMÉLIORATION : Nettoyer toutes les ressources avec gestion ICE optimisée
  async cleanup() {
    try {
      console.log('[WebRTC] 🧹 Nettoyage des ressources...');
      
      // Arrêter les streams locaux
      if (this.localStream) {
        this.localStream.getTracks().forEach(track => {
          track.stop();
          console.log('[WebRTC] 🛑 Piste arrêtée:', track.kind);
        });
        this.localStream = null;
      }

      // Fermer la connexion peer
      if (this.peerConnection) {
        this.peerConnection.close();
        this.peerConnection = null;
      }

      // Se désabonner des listeners Firestore
      if (this.unsubscribeCall) {
        this.unsubscribeCall();
        this.unsubscribeCall = null;
      }
      if (this.unsubscribeOfferCandidates) {
        this.unsubscribeOfferCandidates();
        this.unsubscribeOfferCandidates = null;
      }
      if (this.unsubscribeAnswerCandidates) {
        this.unsubscribeAnswerCandidates();
        this.unsubscribeAnswerCandidates = null;
      }

      // 🔧 NOUVEAU : Marquer l'appel comme terminé dans Firestore si nécessaire
      if (this.callDocRef && this.currentCallId) {
        try {
          await updateDoc(this.callDocRef, {
            status: 'ended',
            endedAt: Date.now()
          });
        } catch (error) {
          console.warn('[WebRTC] ⚠️ Impossible de mettre à jour le statut de fin:', error);
        }
      }

      // Réinitialiser les états
      this.callDocRef = null;
      this.isCallActive = false;
      this.isCaller = false;
      this.currentCallId = null;
      this.remoteStream = null;
      this.iceCandidateQueue = [];
      this.isRemoteDescriptionSet = false;
      this.iceGatheringComplete = false;

      console.log('[WebRTC] ✅ Nettoyage terminé');
    } catch (error) {
      console.error('[WebRTC] ❌ Erreur lors du nettoyage:', error);
    }
  }

  // Vérifier les appels entrants pour un utilisateur
  async checkIncomingCalls(userId, onIncomingCall) {
    console.log('[WebRTC] 🎯 Démarrage checkIncomingCalls pour userId:', userId);
    
    try {
      // 🧹 NETTOYER LES VIEUX APPELS D'ABORD
      await this.cleanupOldCalls(userId);
      
      const callsRef = collection(db, 'calls');
      
      // 🧪 REQUÊTE SIMPLIFIÉE pour éviter le problème d'index
      const q = query(
        callsRef, 
        where('calleeId', '==', userId),
        where('status', '==', 'ringing')
        // ❌ Temporairement retiré : orderBy('createdAt', 'desc')
      );

      console.log('[WebRTC] 📡 Requête SIMPLIFIÉE configurée, démarrage onSnapshot...');

      // 🔧 Set pour éviter les doublons
      const processedCalls = new Set();

      return onSnapshot(q, (snapshot) => {
        console.log('[WebRTC] 📦 Snapshot reçu, docChanges:', snapshot.docChanges().length);
        console.log('[WebRTC] 📦 Total docs dans snapshot:', snapshot.docs.length);
        
        // Log de tous les documents présents
        snapshot.docs.forEach((doc, index) => {
          console.log(`[WebRTC] 📄 Doc ${index}:`, doc.id, doc.data());
        });
        
        snapshot.docChanges().forEach((change) => {
          console.log('[WebRTC] 📄 Document change type:', change.type);
          console.log('[WebRTC] 📄 Document data:', change.doc.data());
          
          // 🔧 GESTION AMÉLIORÉE : Traiter 'added' et 'modified'
          if (change.type === 'added' || change.type === 'modified') {
            const callData = { id: change.doc.id, ...change.doc.data() };
            
            // 🔧 Vérifier l'âge de l'appel (tolérance plus large pour mobiles/onglets suspendus)
            if (typeof callData.createdAt === 'number') {
              const callAge = Date.now() - callData.createdAt;
              const maxAge = 10 * 60 * 1000; // 10 minutes
              
              if (callAge > maxAge) {
                console.log('[WebRTC] ⏰ Appel trop ancien, ignoré:', callAge / 1000, 'secondes');
                return;
              }
            }
            
            // 🔧 Éviter les doublons
            if (processedCalls.has(callData.id)) {
              console.log('[WebRTC] 🔄 Appel déjà traité, ignoré:', callData.id);
              return;
            }
            
            // 🔧 Vérifier que le statut est toujours 'ringing'
            if (callData.status !== 'ringing') {
              console.log('[WebRTC] 📞 Statut non-ringing, ignoré:', callData.status);
              return;
            }
            
            // Marquer comme traité
            processedCalls.add(callData.id);
            
            console.log('[WebRTC] 🔔 APPEL ENTRANT VALIDE DÉTECTÉ:', callData);
            console.log('[WebRTC] 📞 Callback onIncomingCall présent:', !!onIncomingCall);
            
            if (onIncomingCall) {
              console.log('[WebRTC] 📞 Appel du callback...');
              onIncomingCall(callData);
            } else {
              console.error('[WebRTC] ❌ Pas de callback onIncomingCall !');
            }
          }
          
          // 🔧 GESTION DES SUPPRESSIONS : Nettoyer les appels supprimés
          else if (change.type === 'removed') {
            const callId = change.doc.id;
            processedCalls.delete(callId);
            console.log('[WebRTC] 🗑️ Appel supprimé:', callId);
          }
        });
      }, (error) => {
        console.error('[WebRTC] ❌ Erreur onSnapshot:', error);
        console.error('[WebRTC] ❌ Code erreur:', error.code);
        console.error('[WebRTC] ❌ Message erreur:', error.message);
        
        // Vérifier si c'est un problème d'index
        if (error.code === 'failed-precondition') {
          console.error('[WebRTC] ❌ PROBLÈME D\'INDEX FIRESTORE !');
          console.error('[WebRTC] ❌ Créez un index composite pour : calleeId, status, createdAt');
          console.error('[WebRTC] ❌ URL console Firebase: https://console.firebase.google.com/');
        }
        
        // 🔧 En cas d'erreur, nettoyer les références
        processedCalls.clear();
      });
    } catch (error) {
      console.error('[WebRTC] ❌ Erreur lors de la configuration de checkIncomingCalls:', error);
      throw error;
    }
  }

  // 🧹 Nettoyer les vieux appels (plus de 2 minutes)
  async cleanupOldCalls(userId) {
    try {
      console.log('[WebRTC] 🧹 Nettoyage des vieux appels pour:', userId);
      
      const callsRef = collection(db, 'calls');
      const tenMinutesAgo = Date.now() - (10 * 60 * 1000);
      
      const queries = [
        query(callsRef, where('calleeId', '==', userId), where('createdAt', '<', tenMinutesAgo)),
        query(callsRef, where('callerId', '==', userId), where('createdAt', '<', tenMinutesAgo))
      ];

      const docsToDelete = new Map();

      for (const q of queries) {
        const snapshot = await getDocs(q);
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          // Ne supprimer que les appels qui ne sont plus actifs
          if (!data || ['ended', 'rejected', 'missed', 'canceled'].includes(data.status) || data.createdAt < tenMinutesAgo) {
            docsToDelete.set(docSnap.id, docSnap.ref);
          }
        });
      }

      console.log('[WebRTC] 🧹 Vieux appels trouvés pour suppression:', docsToDelete.size);

      if (docsToDelete.size > 0) {
        const deletePromises = Array.from(docsToDelete.values()).map((ref) => deleteDoc(ref));
        await Promise.all(deletePromises);
        console.log('[WebRTC] ✅ Nettoyage terminé');
      } else {
        console.log('[WebRTC] ✅ Aucun vieux appel à nettoyer');
      }
    } catch (error) {
      console.error('[WebRTC] ❌ Erreur nettoyage:', error);
    }
  }

  // Obtenir les statistiques de l'appel
  async getCallStats() {
    if (!this.peerConnection) return null;
    
    try {
      const stats = await this.peerConnection.getStats();
      const result = {};
      
      stats.forEach((report) => {
        if (report.type === 'inbound-rtp' && report.mediaType === 'video') {
          result.inboundVideo = {
            bytesReceived: report.bytesReceived,
            framesReceived: report.framesReceived,
            frameWidth: report.frameWidth,
            frameHeight: report.frameHeight
          };
        }
        if (report.type === 'outbound-rtp' && report.mediaType === 'video') {
          result.outboundVideo = {
            bytesSent: report.bytesSent,
            framesSent: report.framesSent,
            frameWidth: report.frameWidth,
            frameHeight: report.frameHeight
          };
        }
      });
      
      return result;
    } catch (error) {
      console.error('[WebRTC] Erreur lors de la récupération des stats:', error);
      return null;
    }
  }

  // 🔧 NOUVELLE FONCTION : Obtenir la liste des caméras disponibles
  async getAvailableCameras() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      let cameras = devices.filter(device => device.kind === 'videoinput');
      
      // 🔧 FILTRER LES CAMÉRAS PROBLÉMATIQUES
      const blacklistedCameras = ['OBS Virtual Camera', 'ManyCam'];
      cameras = cameras.filter(camera => {
        const label = camera.label || '';
        const isBlacklisted = blacklistedCameras.some(blacklisted => 
          label.includes(blacklisted)
        );
        if (isBlacklisted) {
          console.log('[WebRTC] 🚫 Caméra exclue (blacklist):', label);
        }
        return !isBlacklisted;
      });
      
      console.log('[WebRTC] 📹 Caméras disponibles après filtrage:', cameras);
      return cameras;
    } catch (error) {
      console.error('[WebRTC] Erreur énumération caméras:', error);
      return [];
    }
  }

  // 🔧 NOUVELLE FONCTION : Obtenir la meilleure caméra disponible
  async getBestCamera() {
    const cameras = await this.getAvailableCameras();
    
    if (cameras.length === 0) {
      throw new Error('Aucune caméra valide détectée');
    }
    
    // Préférer les caméras avec des noms connus
    const preferredCameras = ['Logitech', 'Microsoft', 'Realtek', 'HD WebCam', 'USB Camera'];
    
    for (const preferred of preferredCameras) {
      const camera = cameras.find(cam => 
        cam.label && cam.label.toLowerCase().includes(preferred.toLowerCase())
      );
      if (camera) {
        console.log('[WebRTC] 📹 Caméra préférée trouvée:', camera.label);
        return camera;
      }
    }
    
    // Sinon, retourner la première caméra disponible
    console.log('[WebRTC] 📹 Utilisation de la première caméra:', cameras[0].label);
    return cameras[0];
  }

  // 🔧 NOUVELLE FONCTION : Changer de caméra
  async switchCamera(deviceId) {
    try {
      console.log('[WebRTC] 🔄 Changement de caméra vers:', deviceId);
      
      // Arrêter l'ancienne piste vidéo
      if (this.localStream) {
        this.localStream.getVideoTracks().forEach(track => {
          track.stop();
        });
      }
      
      // Obtenir le nouveau stream avec la caméra spécifiée
      const newConstraints = {
        audio: true,
        video: {
          deviceId: { exact: deviceId },
          width: { min: 640, ideal: 1280, max: 1920 },
          height: { min: 480, ideal: 720, max: 1080 },
          frameRate: { min: 15, ideal: 30, max: 60 }
        }
      };
      
      const newStream = await navigator.mediaDevices.getUserMedia(newConstraints);
      
      // Remplacer la piste vidéo dans PeerConnection
      if (this.peerConnection) {
        const videoTrack = newStream.getVideoTracks()[0];
        const sender = this.peerConnection.getSenders().find(
          s => s.track && s.track.kind === 'video'
        );
        
        if (sender) {
          await sender.replaceTrack(videoTrack);
          console.log('[WebRTC] ✅ Piste vidéo remplacée');
        }
      }
      
      // Mettre à jour le stream local
      const audioTrack = this.localStream.getAudioTracks()[0];
      this.localStream = new MediaStream([audioTrack, newStream.getVideoTracks()[0]]);
      
      return this.localStream;
    } catch (error) {
      console.error('[WebRTC] Erreur changement caméra:', error);
      throw error;
    }
  }

  // 🔧 NOUVELLE FONCTION : Détecter l'écran vert
  async detectGreenScreen(stream) {
    try {
      console.log('[WebRTC] 🎨 Début détection écran vert...');
      
      // Créer un élément vidéo temporaire
      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      
      // Attendre que la vidéo soit prête
      await new Promise((resolve) => {
        video.onloadedmetadata = () => {
          video.play();
          resolve();
        };
      });
      
      // Attendre un peu pour avoir des frames
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Créer un canvas pour analyser les pixels
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;
      
      // Capturer une frame
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Analyser les pixels
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;
      
      let greenPixels = 0;
      let totalPixels = pixels.length / 4;
      
      // Parcourir tous les pixels
      for (let i = 0; i < pixels.length; i += 4) {
        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];
        
        // Détecter les pixels verts (vert dominant)
        if (g > 100 && g > r * 1.5 && g > b * 1.5) {
          greenPixels++;
        }
      }
      
      // Nettoyer
      video.pause();
      video.srcObject = null;
      stream.getTracks().forEach(track => track.stop());
      
      const greenPercentage = (greenPixels / totalPixels) * 100;
      console.log(`[WebRTC] 🎨 Pourcentage de pixels verts: ${greenPercentage.toFixed(2)}%`);
      
      // Si plus de 70% de l'image est verte, c'est probablement un écran vert
      return greenPercentage > 70;
      
    } catch (error) {
      console.error('[WebRTC] ❌ Erreur détection écran vert:', error);
      return false;
    }
  }
}

// Instance singleton
const webRTCService = new WebRTCService();
export default webRTCService; 
